import { useEffect, useMemo, useRef } from 'react';
import { Layer, Marker, Source, type MapRef } from 'react-map-gl/maplibre';
import type { InvestigationMarker, InvestigationResult, InvestigationShape } from '@soc/shared';
import { useInvestigationHover } from './investigationHover';

/**
 * The ONE generic map-evidence renderer for every rule's investigation. Consumes
 * the render-agnostic `InvestigationResult`: draws a glyph per marker (dot / ring
 * / diamond) and a `<Source>/<Layer>` per shape (fill / line), and fits the map
 * to `bounds` when the result changes. No per-rule branching — the data decides.
 * Mounted only while investigating; unmounting removes every source/layer/marker.
 */
export function InvestigationLayer({
  result,
  mapRef,
}: {
  result: InvestigationResult;
  mapRef: React.RefObject<MapRef>;
}) {
  const { hovered, setHovered } = useInvestigationHover();

  // Save the map view on ENTER (before any fitBounds) and restore it on EXIT, so
  // closing the investigation zooms back out to exactly where the operator was.
  // Runs once per mount (empty deps) — NOT tied to `result`, so successive
  // fitBounds don't overwrite the saved view.
  const savedViewRef = useRef<{
    center: [number, number];
    zoom: number;
    bearing: number;
    pitch: number;
  } | null>(null);
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const c = map.getCenter();
    savedViewRef.current = {
      center: [c.lng, c.lat],
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    };
    return () => {
      const m = mapRef.current?.getMap();
      const v = savedViewRef.current;
      if (m && v) {
        m.flyTo({ center: v.center, zoom: v.zoom, bearing: v.bearing, pitch: v.pitch, duration: 600 });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fit the map to the evidence when a new result arrives (leave room on the
  // right for the panel). Fall back to the first marker if there are no bounds.
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (result.bounds) {
      map.fitBounds(result.bounds, {
        padding: { top: 60, bottom: 60, left: 60, right: 380 },
        maxZoom: 15,
        duration: 700,
      });
    } else if (result.markers[0]) {
      map.flyTo({ center: [result.markers[0].lng, result.markers[0].lat], zoom: 14, duration: 700 });
    }
  }, [result, mapRef]);

  const markers = useMemo(
    () =>
      result.markers.map((m, i) => (
        <Marker key={i} longitude={m.lng} latitude={m.lat}>
          <Glyph
            marker={m}
            highlighted={m.rowRef != null && m.rowRef === hovered}
            onEnter={() => m.rowRef != null && setHovered(m.rowRef)}
            onLeave={() => setHovered(null)}
          />
        </Marker>
      )),
    [result.markers, hovered, setHovered],
  );

  return (
    <>
      {result.shapes.map((s) => (
        <ShapeLayer key={s.id} shape={s} />
      ))}
      {markers}
    </>
  );
}

/** A single evidence glyph. `dot` = filled circle, `ring` = hollow, `diamond` = rotated square. */
function Glyph({
  marker,
  highlighted,
  onEnter,
  onLeave,
}: {
  marker: InvestigationMarker;
  highlighted: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const size = marker.size ?? (marker.kind === 'dot' ? 10 : 15);
  const glow = highlighted ? 14 : marker.emphasis ? 10 : 6;
  const base: React.CSSProperties = {
    width: size,
    height: size,
    cursor: 'pointer',
    boxShadow: `0 0 ${glow}px ${marker.color}`,
    outline: highlighted ? `2px solid ${marker.color}` : 'none',
    outlineOffset: 1,
    zIndex: highlighted ? 10 : undefined,
  };

  let inner: React.CSSProperties;
  if (marker.kind === 'ring') {
    inner = { ...base, borderRadius: '50%', background: 'transparent', border: `2px solid ${marker.color}` };
  } else if (marker.kind === 'diamond') {
    // Diamond = rotated square. The rotate MUST live on an inner child — MapLibre
    // writes `transform: translate(...)` on the marker element itself.
    return (
      <div style={{ width: size, height: size, cursor: 'pointer' }} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <div
          style={{
            width: '100%',
            height: '100%',
            background: marker.color,
            border: '1.5px solid #fff',
            transform: 'rotate(45deg)',
            boxShadow: `0 0 ${glow}px ${marker.color}`,
            outline: highlighted ? `2px solid ${marker.color}` : 'none',
          }}
        />
      </div>
    );
  } else {
    inner = { ...base, borderRadius: '50%', background: marker.color, border: '1.5px solid #000' };
  }

  return (
    <div
      style={inner}
      title={marker.title}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    />
  );
}

/** A GeoJSON overlay (boundary fill or hull/jump/distance line). */
function ShapeLayer({ shape }: { shape: InvestigationShape }) {
  const data = useMemo(
    () => ({ type: 'Feature' as const, geometry: shape.geometry, properties: {} }),
    [shape.geometry],
  );
  return (
    <Source id={`inv-${shape.id}`} type="geojson" data={data as unknown as GeoJSON.Feature}>
      {shape.render.type === 'fill' ? (
        <Layer
          id={`inv-${shape.id}-fill`}
          type="fill"
          paint={{ 'fill-color': shape.render.color, 'fill-opacity': shape.render.opacity ?? 0.08 }}
        />
      ) : (
        <Layer
          id={`inv-${shape.id}-line`}
          type="line"
          paint={{
            'line-color': shape.render.color,
            'line-width': shape.render.width ?? 2,
            'line-opacity': shape.render.opacity ?? 0.9,
            ...(shape.render.dashed ? { 'line-dasharray': [2, 2] } : {}),
          }}
        />
      )}
    </Source>
  );
}
